import type { AdminInvestorListItem } from '@workspace/api-backend'
import { Search } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useAdminInvestorsQuery } from '../../admin/hooks/use-admin-api'

interface InvestorSearchPickerProps {
  /** Investor IDs already in the allowlist — they're filtered out of suggestions. */
  excludeIds: string[]
  onSelect: (investor: { id: string; name: string }) => void
  placeholder?: string
  disabled?: boolean
}

/** Search-and-pick investor input, the counterpart of `StartupSearchPicker` for investor allowlists. */
export function InvestorSearchPicker({
  excludeIds,
  onSelect,
  placeholder = 'Search investors by name…',
  disabled = false,
}: InvestorSearchPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const trimmed = query.trim()
  const investorsQuery = useAdminInvestorsQuery({
    page: 1,
    limit: 10,
    search: trimmed.length >= 2 ? trimmed : undefined,
  })

  const excluded = new Set(excludeIds)
  const results: AdminInvestorListItem[] =
    investorsQuery.data?.data?.items?.filter((i) => !excluded.has(i.id)) ?? []

  return (
    <div ref={containerRef} className="relative">
      <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        disabled={disabled}
        placeholder={placeholder}
        className="w-full rounded-lg border border-border bg-card py-2 pl-10 pr-4 text-sm outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-[#f97316] disabled:opacity-50"
      />

      {open && trimmed.length >= 2 && (
        <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card shadow-lg">
          {investorsQuery.isLoading && (
            <div className="px-3 py-2 text-xs text-muted-foreground">Searching…</div>
          )}
          {!investorsQuery.isLoading && results.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">No investors match.</div>
          )}
          {results.map((investor) => (
            <button
              key={investor.id}
              type="button"
              onClick={() => {
                onSelect({ id: investor.id, name: investor.name })
                setQuery('')
                setOpen(false)
              }}
              className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-elevated"
            >
              <span className="text-foreground">{investor.name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {investor.id.slice(0, 8)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
